import type { CriteriaEvaluationAnalyzeRequest } from "../api/clinicalTrialsApiTypes";

/** Eligibility states supported by the Criteria Evaluation modal. */
export type CriteriaEvaluationStatus =
  | "Eligible"
  | "Ineligible"
  | "Action Required";

/** Status-specific content displayed by the Criteria Evaluation modal. */
export interface CriteriaEvaluationModalContent {
  tagLabel: CriteriaEvaluationStatus;
  message: string;
  tagBackgroundColor: string;
  tagTextColor: string;
}

const CRITERIA_EVALUATION_CONTENT: Record<
  CriteriaEvaluationStatus,
  CriteriaEvaluationModalContent
> = {
  Eligible: {
    tagLabel: "Eligible",
    message:
      "All evaluated criteria are met with no failures. Initiating will evaluate all criteria according to protocol order.",
    tagBackgroundColor: "#E6F3F0",
    tagTextColor: "#005D49",
  },
  Ineligible: {
    tagLabel: "Ineligible",
    message:
      "One or more evaluation criteria were not met. You may still proceed to record the full evaluation according to protocol order.",
    tagBackgroundColor: "#FEE2E2",
    tagTextColor: "#991B1B",
  },
  "Action Required": {
    tagLabel: "Action Required",
    message:
      "Pending results will be evaluated automatically according to the protocol order once submitted.",
    tagBackgroundColor: "#FFF3E0",
    tagTextColor: "#C24400",
  },
};

/**
 * Convert API and legacy eligibility values into one of the three modal states.
 *
 * @param status - Eligibility status returned by the detail endpoint
 * @returns Canonical status supported by the modal
 */
export function normalizeCriteriaEvaluationStatus(
  status: string,
): CriteriaEvaluationStatus {
  const normalizedStatus = status.trim().toLowerCase();

  if (normalizedStatus === "eligible" || normalizedStatus === "recruited") {
    return "Eligible";
  }
  if (
    normalizedStatus === "ineligible" ||
    normalizedStatus === "likely ineligible" ||
    normalizedStatus === "confirm ineligibility" ||
    normalizedStatus === "low probability"
  ) {
    return "Ineligible";
  }
  return "Action Required";
}

/**
 * Resolve modal copy and tag styling from a canonical eligibility status.
 *
 * @param status - One of the three statuses supported by the modal
 * @returns Status-specific modal content
 */
export function getCriteriaEvaluationModalContent(
  status: CriteriaEvaluationStatus,
): CriteriaEvaluationModalContent {
  return CRITERIA_EVALUATION_CONTENT[status];
}

/**
 * Build the criteria-only analyze request for the current patient.
 *
 * @param mrn - Patient medical record number
 * @returns API request containing the patient and criteria evaluation mode
 */
export function buildCriteriaEvaluationAnalyzeRequest(
  mrn: string,
): CriteriaEvaluationAnalyzeRequest {
  return {
    mrns: [mrn.trim()],
    "evaluation_mode": "criteria",
  };
}

/**
 * Format an analyze failure for the criteria-evaluation error screen.
 *
 * @param error - User-facing error returned by the analysis hook
 * @returns Complete failure sentence
 */
export function formatCriteriaEvaluationErrorMessage(error: string): string {
  const reason = error.trim().replace(/[.]+$/, "") || "system error";
  return `Criteria evaluation failed due to (${reason}).`;
}
